/* SUPABASE AUTH — THE SESSION
 *
 * PHASE 3, STEP 5. Like `src/idb.js`, this file is NEW CODE rather than
 * moved code: `scripts/extract.mjs` does not generate it. Everything
 * else in src/ except idb.js is regenerated from the HTML source, so a
 * hand edit there is silently lost — see BUILD_NOTES.md.
 *
 * ── WHY THIS EXISTS IN PHASE 3 AT ALL ──────────────────────────────
 * The storage swap is defined by RLS, and RLS is defined by
 * `auth.uid()`, which only exists inside a signed JWT issued to a real
 * user. The GRANTs give `profiles` and `holdings` to `authenticated`
 * and `service_role` and NOTHING to `anon` — `rls-check` asserts that
 * denial as a passing check. So a browser holding only the publishable
 * key can neither read nor write a single row. Auth is not a feature
 * that follows the storage swap; it is a precondition of it.
 *
 * ── SCOPE, AND IT IS DELIBERATELY NARROW ───────────────────────────
 * A real session and nothing else: sign in, stay signed in, sign out.
 *
 * NOT HERE, and all of it stays in Phase 5:
 *   - mandatory email verification and its flow
 *   - Google sign-in
 *   - the dormancy / cancellation lifecycle machine
 *   - self-service signup (see BUILD_NOTES: the project currently
 *     requires email confirmation, and that setting is not this step's
 *     to weaken)
 *
 * ── PHASE 5, STEP 3 — 2026-09-09, HIS ORDER: pages → SMTP → signup ──
 * Custom SMTP is now on (step 2, his hand), so the first two items above
 * are built here: `signUp`, `resendConfirmation`, and `landFromUrl` —
 * the handler for a confirmation link returning with tokens in the URL
 * fragment. ⚠ THE LANDING IS PROVIDER-AGNOSTIC ON PURPOSE: Google's
 * `/authorize` returns through the same fragment shape, so step 4 adds
 * a button and reuses this. `mailer_autoconfirm` stays false and is not
 * assumed either way — `signUp` stores a session only if the server
 * minted one, and reports which happened.
 *
 * ── NO DEPENDENCY, DELIBERATELY ────────────────────────────────────
 * `@supabase/supabase-js` would be this project's FIRST production
 * dependency, and the npm-audit acceptance recorded in BUILD_NOTES
 * rests explicitly on there being none (`npm ls --omit=dev` is empty).
 * The surface actually needed here is four HTTP calls against a
 * documented REST API, and the one genuinely awkward part — refresh
 * with rotation — is awkward in a way a library hides rather than
 * removes.
 *
 * This is a judgment call and it is recorded so it can be overruled
 * cheaply: it is one module plus one gate, and swapping in the library
 * behind the same exports would not touch a caller.
 *
 * ── WHERE THE SESSION LIVES ────────────────────────────────────────
 * localStorage, under the `currencykeep:` namespace.
 *
 * It is worth being honest that this is NOT a security win over any
 * other browser option: localStorage, IndexedDB and in-memory-plus-
 * reload are all readable by script running on the page, so all of
 * them fall to XSS equally. The only materially safer design is an
 * httpOnly cookie brokered by a Pages Function — which means the
 * server sits in the path of every Data API call, and that is the
 * opposite shape from an offline-first PWA. supabase-js defaults to
 * localStorage for the same reason.
 *
 * Chosen over IndexedDB because it is synchronous, so a reload knows
 * whether it has a session before the first paint rather than after.
 *
 * NOTHING IN THIS FILE EVER LOGS A TOKEN.
 */

const KEY = 'currencykeep:session'
/* ⚠ THE RECOVERY-PENDING FLAG — his D1 of (94), 2026-10-17: a session a
 * RESET link minted is persisted like any sign-in, and while this key is
 * set every boot opens the new-password screen instead of the vault.
 * ITS OWN KEY, NOT A FIELD ON THE SESSION: `refresh()` rebuilds the
 * session through `shapeSession`, which would drop a field silently an
 * hour in. Cleared by a saved password, a password sign-in and sign-out. */
const RECOVERY_KEY = 'currencykeep:recovery-pending'

/* Refresh this many seconds BEFORE the token actually expires. A token
 * that expires in flight produces a 401 the caller cannot distinguish
 * from a permissions problem — which, on a project whose whole security
 * model is permissions, is the worst possible ambiguity. */
const SKEW_SECONDS = 60

/* Resolved through globalThis first so this module also runs under the
 * gate, where there is no browser and localStorage is a stub. In a
 * browser globalThis IS window, so both lookups find the same object.
 * Same indirection idb.js uses for the IndexedDB factory. */
const store = () =>
  globalThis.localStorage ?? (typeof window !== 'undefined' ? window.localStorage : undefined)

const env = (name) =>
  (typeof import.meta !== 'undefined' && import.meta.env?.[name]) ??
  globalThis.__CK_ENV?.[name] ??
  undefined

const BASE = () => String(env('VITE_SUPABASE_URL') ?? '').replace(/\/$/, '')
const ANON_KEY = () => String(env('VITE_SUPABASE_PUBLISHABLE_KEY') ?? '')

/* ── session state ─────────────────────────────────────────────────── */

let _session = null
let _loaded = false
/* Single-flight refresh. See refresh() for why this is load-bearing
 * rather than an optimisation. */
let _refreshing = null
const _listeners = new Set()

function notify() {
  for (const fn of _listeners) {
    try { fn(_session ? { user: _session.user } : null) } catch (e) { /* a listener must not break auth */ }
  }
}

function load() {
  if (_loaded) return _session
  _loaded = true
  try {
    const raw = store()?.getItem(KEY)
    const parsed = raw ? JSON.parse(raw) : null
    /* Shape-check rather than trust. A truncated or hand-edited value
     * would otherwise surface later as an unreadable 401. */
    _session = parsed && parsed.access_token && parsed.refresh_token && parsed.user?.id ? parsed : null
  } catch (e) {
    _session = null
  }
  return _session
}

function persist(session) {
  _session = session
  _loaded = true
  try {
    if (session) store()?.setItem(KEY, JSON.stringify(session))
    else { store()?.removeItem(KEY); store()?.removeItem(RECOVERY_KEY) }
  } catch (e) {
    /* Private browsing can refuse writes. The session still works for
     * this tab; it simply will not survive a reload. Better than
     * refusing to sign in. */
  }
  notify()
}

/** Supabase returns expires_at in SECONDS. Everything here uses seconds. */
const nowSeconds = () => Math.floor(Date.now() / 1000)

function shapeSession(body) {
  if (!body?.access_token || !body?.refresh_token) return null
  return {
    access_token: body.access_token,
    refresh_token: body.refresh_token,
    /* expires_at is authoritative when present; expires_in is the
     * fallback, because a clock that disagrees with the server by more
     * than the token lifetime would otherwise refresh forever. */
    expires_at: body.expires_at ?? nowSeconds() + (body.expires_in ?? 3600),
    user: { id: body.user?.id, email: body.user?.email },
  }
}

async function authFetch(path, body) {
  const res = await fetch(`${BASE()}/auth/v1/${path}`, {
    method: 'POST',
    headers: { apikey: ANON_KEY(), 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const text = await res.text()
  let parsed = null
  try { parsed = text ? JSON.parse(text) : null } catch (e) { /* not JSON */ }
  return { status: res.status, body: parsed }
}

/* ── the public surface ────────────────────────────────────────────── */

/** The stored session, without touching the network. May be expired. */
function getSession() {
  return load()
}

function isSignedIn() {
  return Boolean(load())
}

function setRecoveryPending(on) {
  try {
    if (on) store()?.setItem(RECOVERY_KEY, '1')
    else store()?.removeItem(RECOVERY_KEY)
  } catch (e) { /* a store that refuses writes: the reset still works for this tab */ }
}

/** True while a session minted by a reset link has not yet saved a new password (D1). */
function isRecoveryPending() {
  try { return Boolean(load()) && store()?.getItem(RECOVERY_KEY) === '1' } catch (e) { return false }
}

/**
 * Sign in with email and password.
 *
 * Throws on failure with a message safe to show a user — never the raw
 * body, which can carry more detail than a signed-out visitor should
 * see. The distinction between "wrong password" and "no such account"
 * is deliberately NOT surfaced: it is an account-enumeration oracle.
 */
async function signIn(email, password) {
  const { status, body } = await authFetch('token?grant_type=password', {
    email: String(email ?? '').trim(),
    password: String(password ?? ''),
  })

  if (status >= 400 || !body?.access_token) {
    /* One case IS worth distinguishing, because it is the project's own
     * configuration rather than the user's mistake, and a user cannot
     * act on "invalid credentials" when the real answer is "you have
     * not clicked the confirmation link". */
    const code = body?.error_code ?? body?.error ?? ''
    if (String(code).includes('email_not_confirmed')) {
      /* `.code` is carried so the sign-in screen can offer the resend
       * path (step 3). The MESSAGE is unchanged: auth-check asserts it. */
      const err = new Error('Check your email and confirm your address before signing in.')
      err.code = 'email_not_confirmed'
      throw err
    }
    throw new Error('That email and password do not match.')
  }

  const session = shapeSession(body)
  if (!session) throw new Error('Sign-in returned an unusable session.')
  persist(session)
  /* a person who signs in WITH their password knows it — no reset is pending */
  setRecoveryPending(false)
  return { user: session.user }
}

/* ── PHASE 5 STEP 3 — sign-up, resend, and the landing ─────────────── */

/* Where a confirmation link should bring the browser back to. In a
 * browser it is THIS origin's root — both hosts are in Supabase's
 * Redirect URL allow-list (step 2, his hand, 2026-09-09) — so a link
 * clicked on the phone lands on the host the account was made on.
 * Under the gate there is no `location`; the server then falls back to
 * its Site URL, which is the apex. */
const redirectTo = (loc = globalThis.location) => {
  return loc?.origin && /^https?:/.test(String(loc.origin)) ? `${loc.origin}/` : null
}
const withRedirect = (path) => {
  const to = redirectTo()
  return to ? `${path}?redirect_to=${encodeURIComponent(to)}` : path
}

/* ── GOOGLE — Phase 5 step 4, 2026-09-10 ────────────────────────────
 * One button, no library. The browser is sent to Supabase's `/authorize`
 * with `provider=google`; Supabase sends it on to Google, takes the
 * callback itself, and returns the browser to `redirect_to` carrying
 * the SAME fragment shape the confirmation link uses (implicit flow:
 * `#access_token=…&refresh_token=…`, no `type`). So `landFromUrl` lands
 * it unchanged — that was the point of building the landing first.
 *
 * ⚠ THE ONE THING THE LANDING CANNOT READ OFF THE FRAGMENT IS WHERE IT
 * CAME FROM. An error fragment from Google's consent screen
 * (`#error=access_denied…`) and one from a spent email link are the same
 * bytes; told the email sentence, a person who pressed Cancel at Google
 * would be sent to the resend-a-confirmation form. So the outbound step
 * leaves a marker in sessionStorage — same tab only, gone when the tab
 * closes — and the landing reads AND CLEARS it on every boot, ours or
 * not, so it can only ever describe the return trip happening now.
 * Under Node there is no sessionStorage; the gate hands in a fake, and
 * the app without one still signs in — only the error wording loses
 * the provider.
 */
const OAUTH_MARKER = 'currencykeep:oauth-pending'
const oauthStore = () => globalThis.sessionStorage ?? null

function googleSignInUrl(loc = globalThis.location) {
  const to = redirectTo(loc)
  if (!to) return null
  return `${BASE()}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(to)}`
}

/** Leave the marker, then go. Throws (and goes nowhere, marks nothing)
 * when there is no web origin to come back to. */
function beginGoogleSignIn(loc = globalThis.location, store = oauthStore()) {
  const url = googleSignInUrl(loc)
  if (!url) throw new Error('Google sign-in has to be started from the app’s own web address.')
  try { store?.setItem?.(OAUTH_MARKER, 'google') } catch (e) { /* no sessionStorage: sign-in still lands; the cancel wording loses the provider */ }
  loc.assign(url)
  return url
}

function takeOauthMarker(store) {
  try {
    const v = store?.getItem?.(OAUTH_MARKER) ?? ''
    if (v) store.removeItem(OAUTH_MARKER)
    return String(v || '')
  } catch (e) {
    return ''
  }
}

const PROVIDER_ERRORS = {
  access_denied: 'Google sign-in was cancelled. Try again, or sign in with your email and password.',
}
const PROVIDER_FALLBACK = 'Google sign-in did not finish. Try again, or sign in with your email and password.'

const PENDING_MESSAGE = 'Check your email for a link to confirm your address.'

/**
 * Create an account. Resolves to one of two results:
 *   { status: 'confirm',   email }  — a confirmation mail is on its way
 *   { status: 'signed_in', user }   — the server minted a session (only
 *                                     if confirmation is switched off)
 *
 * ⚠ AN EXISTING ADDRESS GETS EXACTLY THE SAME ANSWER AS A NEW ONE. With
 * confirmation required, Supabase answers a duplicate sign-up with a
 * faux user rather than an error, precisely so the form is not an
 * account-enumeration oracle; the person who already has an account
 * finds no mail and signs in instead. If the server ever DOES say
 * `user_already_exists` (it only can with confirmation off), that is
 * folded into the same 'confirm' result for the same reason — the
 * message stays true in the only way that leaks nothing.
 *
 * Throws with a message safe to show. The password-policy reason is the
 * one server message passed through verbatim: it describes OUR policy,
 * not the person's account.
 */
async function signUp(email, password) {
  const { status, body } = await authFetch(withRedirect('signup'), {
    email: String(email ?? '').trim(),
    password: String(password ?? ''),
  })

  if (status >= 400) {
    const code = String(body?.error_code ?? body?.code ?? body?.error ?? '')
    const msg = String(body?.msg ?? body?.message ?? body?.error_description ?? '')
    if (code === 'user_already_exists') return { status: 'confirm', email: String(email ?? '').trim() }
    if (code === 'weak_password' || (/password/i.test(msg) && status === 422)) {
      throw new Error(msg || 'That password is too weak.')
    }
    if (code === 'over_email_send_rate_limit' || status === 429) {
      throw new Error('Too many emails were sent to that address just now. Wait a minute, then try again.')
    }
    if (/sending confirmation/i.test(msg) || /email/i.test(msg) && status >= 500) {
      /* Our mailer, not their mistake — the same reasoning as the
       * email_not_confirmed case in signIn. */
      throw new Error('We could not send the confirmation email. Try again in a moment, or get in touch.')
    }
    if (code === 'validation_failed' || /email/i.test(msg)) throw new Error('Enter a valid email address.')
    throw new Error('Could not create the account. Try again in a moment.')
  }

  if (body?.access_token) {
    const session = shapeSession(body)
    if (session) { persist(session); return { status: 'signed_in', user: session.user } }
  }
  return { status: 'confirm', email: String(email ?? '').trim() }
}

/**
 * Ask for the confirmation mail again.
 *
 * Resolves to true when the server accepted the request — which it does
 * for an address it has never seen, for the same non-enumeration reason
 * as above, so the screen's wording is conditional: "if that address
 * has an unconfirmed account, a new email is on its way". Throws only
 * on things the person can act on: a malformed address, the one-minute
 * send limit, or our mailer being down.
 */
async function resendConfirmation(email) {
  const { status, body } = await authFetch(withRedirect('resend'), {
    type: 'signup',
    email: String(email ?? '').trim(),
  })
  if (status < 400) return true
  const code = String(body?.error_code ?? body?.code ?? body?.error ?? '')
  const msg = String(body?.msg ?? body?.message ?? '')
  if (code === 'over_email_send_rate_limit' || status === 429) {
    throw new Error('A confirmation email went to that address less than a minute ago. Wait a minute, then try again.')
  }
  if (code === 'validation_failed' || (status < 500 && /email/i.test(msg))) throw new Error('Enter a valid email address.')
  if (status >= 500) throw new Error('We could not send the email right now. Try again in a moment.')
  return true
}

/* ── THE RESET — his rulings (94) and (97), 2026-10-17 ───────────────
 * ⚠ NO "PLEASE WAIT" — his ruling (97), A, superseding D3's neutral
 * sentence: `signup-check` §4(a) measured that the server throttles ONLY
 * an address that has an account (the 60 s per-user interval) and never
 * an unknown one, so any limit sentence told the two apart. A
 * rate-limited request now answers exactly as a sent one. */
const RESET_DEAD_LINK_MESSAGE = 'That reset link has expired or was already used. Enter your email and we\'ll send a new one.'
const SAME_PASSWORD_MESSAGE = 'That\'s your current password. Choose a different one.'
/* his correction, 2026-10-17 — shown on the new-password screen after a save
 * whose sign-out of the other devices failed twice */
const OTHERS_NOT_SIGNED_OUT_MESSAGE = 'Your password is changed. We couldn\'t sign you out of your other devices — sign out on them yourself, or try changing your password again later.'

/**
 * Ask for a password-reset mail. Resolves to true whenever the server
 * accepted the request — which it does for an address it has never seen
 * (Supabase's documented behaviour; `signup-check` §4 asserts it), so the
 * screen's sentence is conditional, as `resendConfirmation`'s is.
 * ⚠ A RATE-LIMITED REQUEST RESOLVES TO TRUE TOO — his ruling (97), A: the
 * per-user interval refuses only a KNOWN address (measured 2026-10-18), so
 * an error here would be the account oracle. The project's hourly limit is
 * treated alike unless the server's message tells the two apart (measured
 * on the next run); the trade-off — a mail the project limit stopped is
 * answered as sent — is recorded. ⚠ Supabase's own `/recover` still
 * answers the two differently; the app cannot change that.
 * Throws only on what the person can act on and that does not depend on
 * whether an account exists: a malformed address, or our mailer being down.
 */
async function requestPasswordReset(email) {
  const { status, body } = await authFetch(withRedirect('recover'), {
    email: String(email ?? '').trim(),
  })
  /* ⚠ ORDERED UNLIKE `resendConfirmation` ON PURPOSE: that function's
   * two-line "accepted, else read the code" sequence is `signup-sabotage`'s
   * anchor, and a copy of it here made the anchor match twice (2026-10-17). */
  const code = String(body?.error_code ?? body?.code ?? body?.error ?? '')
  const msg = String(body?.msg ?? body?.message ?? '')
  if (status < 400) return true
  if (code === 'over_email_send_rate_limit' || status === 429) return true
  if (code === 'validation_failed' || (status < 500 && /email/i.test(msg))) throw new Error('Enter a valid email address.')
  if (status >= 500) throw new Error('We could not send the email right now. Try again in a moment.')
  return true
}

/* Fragment error codes, said in words the person can act on. Anything
 * unlisted gets the last line. */
const LANDING_ERRORS = {
  otp_expired: 'That confirmation link has expired or was already used. Enter your email and we will send a new one.',
  access_denied: 'That confirmation link could not be used. Enter your email and we will send a new one.',
  invalid_token: 'That link did not sign you in. Enter your email and we will send a new one.',
}
/* The sentence depends on where the fragment came from — see the
 * marker above. Same code, two very different things to do next. */
const landingMessage = (provider, code) => provider
  ? (PROVIDER_ERRORS[code] ?? PROVIDER_FALLBACK)
  : (LANDING_ERRORS[code] ?? LANDING_ERRORS.invalid_token)

/* ⚠ THE TOKEN-HASH LINK — 2026-10-10, his rulings. The confirmation email
 * links to OUR site, `{{ .SiteURL }}/#token_hash=…&type=email`, instead of
 * to the Supabase project's /verify, so the link in the mail is on
 * currencykeep.com — the domain the mail is signed as — without the paid
 * custom auth domain. Opening the link spends nothing: the hash is
 * exchanged here, by one POST to /auth/v1/verify, after the caller has
 * already cleaned the URL. `type=email` is his ruling, on his reading of
 * Supabase's verifyOtp reference (`signup` is deprecated there for
 * email); signup-check's first run is what confirms the server takes it.
 *
 * ⚠ THE SESSION COMES FROM THE SERVER'S ANSWER TO THE POST, never from the
 * URL — the fragment carries only the hash, worthless without the
 * exchange — so no second GET /user is needed to trust it. A refusal (a
 * hash already used, expired, or forged) is said in the same sentences as
 * a dead `#access_token` link. */
async function landTokenHash(tokenHash, type, provider) {
  let res = null
  let answer = null
  try {
    res = await fetch(`${BASE()}/auth/v1/verify`, {
      method: 'POST',
      headers: { apikey: ANON_KEY(), 'content-type': 'application/json' },
      body: JSON.stringify({ type, token_hash: tokenHash }),
    })
    answer = await res.json().catch(() => null)
  } catch (e) {
    return { status: 'error', code: 'network', type, provider, message: 'Could not finish confirming your address. Check your connection and open the link again.' }
  }
  if (!res.ok || !answer?.access_token) {
    const refused = String(answer?.error_code ?? answer?.code ?? 'invalid_token')
    return { status: 'error', code: refused, type, provider, message: landingMessage(provider, refused) }
  }
  const session = shapeSession(answer)
  if (!session?.user?.id) return { status: 'error', code: 'invalid_token', type, provider, message: landingMessage(provider, 'invalid_token') }
  persist(session)
  return { status: 'signed_in', type, provider, user: session.user }
}

/**
 * THE LANDING. Reads a `#access_token=…&refresh_token=…&type=signup`
 * fragment — what Supabase's /verify redirect (and, in step 4, its
 * /authorize redirect) leaves in the URL — and turns it into a session.
 * Since 2026-10-10 it also reads `#token_hash=…&type=email`, the link his
 * email template carries, and exchanges it in `landTokenHash` above; the
 * `#access_token` path is unchanged, for Google and for any link already
 * sent the old way.
 *
 * Resolves to null when the fragment is not ours (no tokens, no error),
 * touching nothing and making no request; otherwise to
 *   { status: 'signed_in', type, user }   or
 *   { status: 'error', code, type, message }
 *
 * ⚠ THE FRAGMENT IS REMOVED FROM THE URL FIRST, BEFORE ANY REQUEST. A
 * token in the address bar is a token in the history, in a screenshot,
 * and in whatever the tab is shared to; and a reload must not try to
 * land twice. The strip does not depend on the exchange succeeding.
 *
 * ⚠ THE TOKEN IS NEVER TRUSTED FROM THE URL. Anyone can type a fragment.
 * The access token is presented to `GET /auth/v1/user`, and the session
 * is stored only if the server answers with the user it belongs to —
 * which is also where the user id and email come from, since the
 * fragment carries neither.
 *
 * `loc` and `hist` are parameters so the gate can hand in a fake
 * location; in the browser they default to the real ones.
 */
async function landFromUrl(loc = globalThis.location, hist = globalThis.history, store = oauthStore()) {
  return asRecovery(await landAny(loc, hist, store))
}

/* ⚠ A RESET LINK IS NOT A SIGN-IN — his ruling (94), D1. The landing
 * below exchanges a `type=recovery` link exactly as it does a
 * confirmation (the same two paths, the same server checks); THIS is
 * where it becomes a reset: a session it produced is marked pending and
 * reported as `recovery`, never `signed_in`, so the caller opens the
 * new-password screen; and a dead reset link is said in reset words and
 * routed to the reset form, not to the resend-a-confirmation one. It
 * wraps the result rather than editing the paths, so a confirmation and
 * a Google return are untouched by construction. */
function asRecovery(r) {
  if (!r || r.type !== 'recovery') return r
  if (r.status === 'signed_in') { setRecoveryPending(true); return { ...r, status: 'recovery' } }
  if (r.status === 'error') {
    return { ...r, message: r.code === 'network'
      ? 'Could not finish opening your reset link. Check your connection and open the link again.'
      : RESET_DEAD_LINK_MESSAGE }
  }
  return r
}

async function landAny(loc, hist, store) {
  /* Consumed on EVERY boot, before anything else is read — a marker left
   * by a trip that never returned must not describe the next one. */
  const provider = takeOauthMarker(store)
  const hash = String(loc?.hash ?? '')
  if (hash.length < 2) return null
  const p = new URLSearchParams(hash.slice(1))
  const hasTokens = p.has('access_token') && p.has('refresh_token')
  const hasError = p.has('error') || p.has('error_code') || p.has('error_description')
  const tokenHash = p.get('token_hash') ?? ''
  if (!hasTokens && !hasError && !tokenHash) return null

  const clean = `${loc?.pathname || '/'}${loc?.search || ''}`
  try { hist?.replaceState?.(null, '', clean) } catch (e) { /* a page that cannot rewrite its URL still lands */ }

  const type = p.get('type') ?? ''
  if (hasError) {
    const code = p.get('error_code') || p.get('error') || 'unknown'
    return { status: 'error', code, type, provider, message: landingMessage(provider, code) }
  }
  if (tokenHash) return landTokenHash(tokenHash, type, provider)

  const access_token = p.get('access_token')
  const refresh_token = p.get('refresh_token')
  let user = null
  try {
    const res = await fetch(`${BASE()}/auth/v1/user`, {
      headers: { apikey: ANON_KEY(), authorization: `Bearer ${access_token}` },
    })
    if (res.ok) user = await res.json().catch(() => null)
  } catch (e) {
    return {
      status: 'error', code: 'network', type, provider,
      message: provider
        ? 'Could not finish signing in with Google. Check your connection and try again.'
        : 'Could not finish confirming your address. Check your connection and open the link again.',
    }
  }
  if (!user?.id) return { status: 'error', code: 'invalid_token', type, provider, message: landingMessage(provider, 'invalid_token') }

  const expiresAt = Number(p.get('expires_at'))
  const expiresIn = Number(p.get('expires_in'))
  const session = shapeSession({
    access_token,
    refresh_token,
    expires_at: Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : undefined,
    expires_in: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : undefined,
    user,
  })
  if (!session) return { status: 'error', code: 'invalid_token', type, provider, message: landingMessage(provider, 'invalid_token') }
  persist(session)
  return { status: 'signed_in', type, provider, user: session.user }
}

/**
 * Exchange the refresh token for a new pair.
 *
 * ⚠ SINGLE-FLIGHT, AND THAT IS NOT AN OPTIMISATION.
 * Supabase ROTATES refresh tokens: a successful refresh invalidates the
 * token it was called with. So two concurrent refreshes race — the
 * second presents a token the first has already spent, is refused, and
 * the user is signed out for no reason. The app makes many storage
 * calls at once (state, photos, profile all load together), so
 * concurrent refresh is the normal case here rather than a rare one.
 *
 * Every caller therefore awaits the SAME promise. Same idiom as the
 * cached open-promise in idb.js and the write chain in storage.js.
 */
/* ⚠ WHICH ANSWERS END A SESSION — register item, his go 2026-10-18. The
 * server REFUSING this refresh token: any 4xx but 429 — `remote.js`
 * `isPermanent`'s line, except a 401, which on the token endpoint is a
 * refusal (there is no refresh to retry with). A 429, a 5xx or a 2xx
 * without both tokens keeps the session (below). Measured refusals on
 * production: 400, three times (`auth-check`, `signup-check` §4(f)).
 * `search-check` E1, F1–F4; `search-sabotage` SB74–SB78. */
const refreshRefused = (status) => status >= 400 && status < 500 && status !== 429

function refresh() {
  if (_refreshing) return _refreshing

  _refreshing = (async () => {
    const current = load()
    if (!current?.refresh_token) return null

    const { status, body } = await authFetch('token?grant_type=refresh_token', {
      refresh_token: current.refresh_token,
    })

    if (refreshRefused(status)) {
      /* The refresh token is spent, revoked, or the user was deleted.
       * CLEAR the session rather than leaving a stale one in place:
       * keeping it would retry on every single call forever, and every
       * one of those failures would look like a permissions error. */
      persist(null)
      return null
    }

    const next = status >= 200 && status < 300 ? shapeSession(body) : null
    if (!next) {
      /* A rate limit, an outage, or an answer with no tokens: the session
       * STAYS, and the caller sees a failure — never null, which every
       * caller reads as signed out. The same shape as a network failure.
       * The message is a sentence because `setNewPassword` calls
       * `getToken()` outside its try, so `doSavePass` shows it. */
      throw Object.assign(new Error('Could not reach the sign-in server. Try again in a moment.'), { status, transient: true })
    }
    persist(next)
    return next
  })()

  /* Release the single-flight slot however it ends, so a network blip
   * does not wedge auth for the life of the page. */
  _refreshing.catch(() => {}).finally(() => { _refreshing = null })

  return _refreshing
}

/**
 * A usable access token, refreshed if it is close to expiring.
 * Returns null when there is no session — callers must treat that as
 * signed-out rather than as an error.
 *
 * THIS is what the Phase 3 storage backend calls on every request.
 */
async function getToken() {
  const session = load()
  if (!session) return null
  if (session.expires_at - nowSeconds() > SKEW_SECONDS) return session.access_token
  const next = await refresh()
  return next?.access_token ?? null
}

/**
 * Sign out. Clears locally FIRST and unconditionally.
 *
 * The revoke call is best-effort: if it fails, the user still expects
 * to be signed out on this device, and a sign-out that can fail is a
 * sign-out nobody trusts. The access token expires on its own.
 */
async function signOut() {
  const session = load()
  persist(null)
  if (!session?.access_token) return
  try {
    await fetch(`${BASE()}/auth/v1/logout`, {
      method: 'POST',
      headers: {
        apikey: ANON_KEY(),
        authorization: `Bearer ${session.access_token}`,
      },
    })
  } catch (e) {
    /* Already signed out locally, which is what was asked for. */
  }
}

/**
 * Save a new password on the session a reset link produced — his ruling
 * (94). `PUT /auth/v1/user` with the session's bearer token; on success
 * the pending flag is cleared (D1) and EVERY OTHER SESSION IS SIGNED OUT,
 * always (D2): `POST /auth/v1/logout?scope=others`, best-effort like
 * `signOut` — this device's session stays. Resolves to
 * `{ ok: true, othersSignedOut }`; throws with a sentence safe to show.
 * The password's strength is the SERVER's policy, its reason passed
 * through verbatim as sign-up does; only the same-password refusal is
 * said in our words.
 */
async function setNewPassword(password) {
  const token = await getToken()
  if (!token) throw new Error('This reset has run out. Ask for a new reset email.')
  let res = null
  let body = null
  try {
    res = await fetch(`${BASE()}/auth/v1/user`, {
      method: 'PUT',
      headers: { apikey: ANON_KEY(), authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ password: String(password ?? '') }),
    })
    body = await res.json().catch(() => null)
  } catch (e) {
    throw new Error('Could not save the password. Check your connection and try again.')
  }
  if (!res.ok) {
    const code = String(body?.error_code ?? body?.code ?? body?.error ?? '')
    const msg = String(body?.msg ?? body?.message ?? body?.error_description ?? '')
    if (code === 'same_password') throw new Error(SAME_PASSWORD_MESSAGE)
    if (code === 'weak_password' || (/password/i.test(msg) && res.status === 422)) throw new Error(msg || 'That password is too weak.')
    throw new Error('Could not save the password. Try again in a moment.')
  }
  setRecoveryPending(false)
  /* ⚠ NOT SILENT, his correction 2026-10-17: whether a password change
   * alone ends the other devices' sessions is NOT established — their
   * refresh tokens can outlive the access token unless Supabase revokes
   * them on a password change, and `signup-check` §4(f) is what measures
   * it. So the call is tried TWICE, and a second failure is reported, so
   * the screen can tell the person to sign out on those devices. */
  const othersOnce = async () => {
    try {
      const out = await fetch(`${BASE()}/auth/v1/logout?scope=others`, {
        method: 'POST',
        headers: { apikey: ANON_KEY(), authorization: `Bearer ${token}` },
      })
      return out.ok
    } catch (e) { return false }
  }
  const othersSignedOut = (await othersOnce()) || (await othersOnce())
  return { ok: true, othersSignedOut }
}

/** Subscribe to sign-in / sign-out. Returns an unsubscribe function. */
function onChange(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

/** Test seam: drop cached state so a gate can start from nothing. */
function _reset() {
  _session = null
  _loaded = false
  _refreshing = null
}

export {
  KEY,
  OAUTH_MARKER,
  OTHERS_NOT_SIGNED_OUT_MESSAGE,
  PENDING_MESSAGE,
  RECOVERY_KEY,
  RESET_DEAD_LINK_MESSAGE,
  SAME_PASSWORD_MESSAGE,
  SKEW_SECONDS,
  beginGoogleSignIn,
  getSession,
  getToken,
  googleSignInUrl,
  isRecoveryPending,
  isSignedIn,
  landFromUrl,
  onChange,
  refresh,
  requestPasswordReset,
  resendConfirmation,
  setNewPassword,
  signIn,
  signOut,
  signUp,
  _reset,
}
